// début du fichier
import React, { useState, useEffect } from "react";
import Button from "../../components/Button/Button";
import { useNavigate } from "react-router-dom";
import { getGroupsHome } from "../../services/groupServices";
import { getSettings } from "../../services/settingsServices";
import { getAdherentsByGroup } from "../../services/dashboardServices";
import { getDojos } from "../../services/dojoServices";
import { useUser } from "./../Home/UserContext";

const GroupTable = () => {
  const { user } = useUser();
  const [groups, setGroups] = useState([]);
  const [openGroupIndex, setOpenGroupIndex] = useState(null);
  const [dojos, setDojos] = useState([]);
  const [saisonActive, setSaisonActive] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [dojoData, groupsData, adherentsData, settingsData] =
          await Promise.all([
            getDojos(),
            getGroupsHome(),
            getAdherentsByGroup(),
            getSettings(),
          ]);

        const combinedGroups = groupsData.map((group) => {
          const adherentsInfo = adherentsData.find(
            (ad) => ad.nom === group.nom
          );
          return {
            ...group,
            adherentCount: adherentsInfo?.adherentCount || 0,
            quota_groupe: adherentsInfo?.quota_groupe || 0,
          };
        });

        setDojos(dojoData);
        setGroups(combinedGroups);
        setSaisonActive(settingsData?.[0]?.saison_active || "");
      } catch (error) {
        console.error("Erreur lors du chargement :", error);
      }
    };

    fetchAll();
  }, []);

  const handleButtonClick = () => {
    if (user) {
      navigate("/ajouter-inscription-adherent");
    } else {
      navigate("/register");
    }
  };

  const getButtonText = () => {
    return user
      ? "Faire une inscription ou cours d'essai"
      : "S'inscrire pour faire une adhésion";
  };

  const getLightColorClass = (color) => {
    const colorMap = {
      "bg-orange-400": "bg-orange-200",
      "bg-indigo-400": "bg-indigo-200",
      "bg-purple-400": "bg-purple-200",
      "bg-green-400": "bg-green-200",
      "bg-yellow-400": "bg-yellow-200",
      "bg-blue-400": "bg-blue-200",
      "bg-red-400": "bg-red-200",
      "bg-pink-400": "bg-pink-200",
      "bg-gray-400": "bg-gray-200",
      "bg-cyan-400": "bg-cyan-200",
    };
    return colorMap[color] || "bg-gray-200";
  };

  const getFillStyles = (percentage, color) => {
    const lightColor = `${color}80`;
    return {
      background: `linear-gradient(to right, ${color} ${percentage}%, ${lightColor} ${percentage}%)`,
    };
  };

  // Fonction pour ouvrir la modale avec l'image sélectionnée
  const openModal = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Fonction pour fermer la modale
  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <section className="py-12 px-4 bg-lightBackground dark:bg-darkBackground text-black dark:text-white">
      <h2 className="text-3xl font-extrabold mb-4 text-primary mx-auto text-center font-satoshi uppercase">
        DOJOS - GROUPES - HORAIRES - TARIFS
      </h2>
      {saisonActive && (
        <h3 className="text-xl text-center mb-8 mt-2 font-satoshi">
          pour la saison : {saisonActive}
        </h3>
      )}

      {dojos.map((dojo) => {
        const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
          dojo.dojo_adresse
        )}&output=embed`;
        const dojoGroups = groups.filter((g) => g.dojo_id === dojo.id);

        return (
          <div key={dojo.id} className="mb-16 max-w-[1280px] mx-auto">
            {/* Carte dojo + map */}
            <div className="flex flex-col md:flex-row gap-6">
              <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden md:w-1/2">
                <img
                  src={dojo.dojo_photoURL || "/img/no-image.jpg"}
                  alt={dojo.dojo_nom}
                  className="w-full h-40 object-cover"
                  onClick={() =>
                      openModal(dojo.dojo_photoURL || "/img/no-image.jpg")
                    } // Ouvre la modale avec l'image par défaut si aucune image
                />
                <div className="p-4 text-center">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    {dojo.dojo_nom}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {dojo.dojo_discipline}
                  </p>
                </div>
              </div>

              <div className="md:w-1/2">
                <iframe
                  title={`Map ${dojo.dojo_nom}`}
                  src={mapUrl}
                  width="100%"
                  height="200"
                  className="rounded-md border"
                  loading="lazy"
                />
                <p className="mt-2 text-sm text-gray-800 dark:text-gray-300">
                  <strong>Adresse :</strong> {dojo.dojo_adresse}
                </p>
              </div>
              {/* Modale pour afficher l'image en grand */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <div className="relative">
              <img
                src={selectedImage}
                alt="Agrandissement"
                className="max-w-full max-h-[80vh] rounded-lg"
              />
              <button
                className="absolute top-2 right-2 text-white text-2xl"
                onClick={closeModal}
                aria-label="Fermer"
              >
                &times;
              </button>
            </div>
          </div>
        )}
            </div>

            {/* Groupes du dojo */}
            <div className="mt-6">
              {dojoGroups.map((group, index) => {
                const percentage =
                  group.quota_groupe > 0
                    ? Math.round(
                        (group.adherentCount / group.quota_groupe) * 100
                      )
                    : 0;

                return (
                  <div
                    key={group.id}
                    className="collapse collapse-arrow border mb-8 font-satoshi border-gray-400 dark:border-gray-400 shadow-lg"
                  >
                    <div className="relative h-8 overflow-hidden flex">
                      <div
                        className={`${group.color_groupe} h-full`}
                        style={{ width: `${percentage}%` }}
                      />
                      <div
                        className={`h-full ${getLightColorClass(
                          group.color_groupe
                        )}`}
                        style={{ width: `${100 - percentage}%` }}
                      />
                    </div>

                    <input
                      type="checkbox"
                      className="peer hidden"
                      id={`group-${group.id}`}
                    />
                    <label
                      htmlFor={`group-${group.id}`}
                      className="collapse-title text-xl font-medium cursor-pointer bg-gray-200 dark:bg-gray-400 dark:text-gray-800 shadow-xl"
                    >
                      <div
                        className="rounded-lg p-1"
                        style={getFillStyles(
                          percentage,
                          group.color_groupe || "#ccc"
                        )}
                      >
                        {group.nom} |{" "}
                        <span className="italic font-light text-base">
                          {group.annees_naissance}
                        </span>
                      </div>
                    </label>

                    <div className="collapse-content bg-white text-gray-800 dark:bg-gray-600 dark:text-white">
                      <p>
                        <strong>Horaire 1 :</strong> {group.horaire1}
                      </p>
                      {group.horaire2 && (
                        <p>
                          <strong>Horaire 2 :</strong> {group.horaire2}
                        </p>
                      )}
                      <p>
                        <strong>Enseignant(s) :</strong>{" "}
                        {[
                          group.enseignant1,
                          group.enseignant2,
                          group.enseignant3,
                          group.enseignant4,
                          group.enseignant5,
                          group.enseignant6,
                        ]
                          .filter(Boolean)
                          .join(" & ")}
                      </p>
                      <p>
                        <strong>Tarifs :</strong> {group.tarif_licence}€ Licence
                        - {group.tarif_cotisation}€ Cotisation
                      </p>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `<strong>Informations Groupes :</strong> ${group.info_groupe}`,
                        }}
                      />
                      <p>
                        <strong>Tarif Total Annuel :</strong>{" "}
                        {parseFloat(group.tarif_licence) +
                          parseFloat(group.tarif_cotisation)}
                        €
                      </p>
                      <p>
                        <strong>Inscrits :</strong> {group.adherentCount} /{" "}
                        {group.quota_groupe}{" "}
                        <span className="italic">({percentage}% rempli)</span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <Button
        text={getButtonText()}
        onClick={handleButtonClick}
        className="mx-auto"
      />
    </section>
  );
};

export default GroupTable;
